import eq from "lodash-es/eq"
import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"

import type {
    IAttendanceSessionDto,
    IAttendanceSessionModel,
    IAttendanceSessionParticipantDto,
    IAttendanceSessionParticipantModel,
    ICoachPaginatedSessionDto,
    ICoachPaginatedSessionModel,
    ICoachingSessionDto,
    ICoachingSessionModel,
    ICoachingSessionsQueryDto,
    ICoachingSessionsQueryModel,
    IGroupSessionSummaryConfigDto,
    IGroupSessionSummaryConfigModel,
    ISessionBaseDto,
    ISessionBaseModel,
    ISessionRescheduleRecordDto,
    ISessionRescheduleRecordModel,
    TMarkedSessionAttendanceStatusDto,
    TSessionAttendanceStatusDto,
    TSessionEventStatus,
    TSessionModality,
    TSessionTypeSlugDto
} from "@/3514/types"
import {
    ESessionAttendanceStatus,
    ESessionEventStatus,
    ESessionInvitationStatus,
    ESessionModality,
    ESessionTypeSlug
} from "@/3514/types"
import { getSessionNameEnumFromType } from "@/3514/utils"

import { coachingSessionCoachAdapter } from "./coach.adapter"
import { attendanceSessionCohortAdapter, getCohortModalityEnumFromType } from "./cohort.adapter"
import {
    attendanceSessionParticipantAdapter,
    coachSessionParticipantsAdapter,
    coachingSessionParticipantsAdapter,
    extraPartiesAdapter
} from "./participant.adapter"

function getSessionInvitationStatusEnumFromType(status: TSessionAttendanceStatusDto): ESessionInvitationStatus {
    switch (status) {
        case "accepted":
            return ESessionInvitationStatus.Accepted
        case "declined":
            return ESessionInvitationStatus.Declined
        case "tentative":
            return ESessionInvitationStatus.Tentative
        case "pending":
            return ESessionInvitationStatus.Pending
        case "present":
            return ESessionInvitationStatus.Present
        case "not_present":
            return ESessionInvitationStatus.NotPresent
        case null:
        default:
            return null
    }
}

function getSessionAttendanceStatusEnumFromType(status: TMarkedSessionAttendanceStatusDto): ESessionAttendanceStatus {
    switch (status) {
        case "present":
            return ESessionAttendanceStatus.Present
        case "not_present":
            return ESessionAttendanceStatus.NotPresent
        case null:
        default:
            return null
    }
}

function getSessionModalityEnumFromType(type: TSessionModality): ESessionModality {
    switch (type) {
        case "group":
            return ESessionModality.Group
        case "individual":
            return ESessionModality.Individual
        default:
            return null
    }
}

function getSessionEventStatusEnumFromType(type: TSessionEventStatus): ESessionEventStatus {
    switch (type) {
        case "scheduled":
            return ESessionEventStatus.Scheduled
        case "not_scheduled":
            return ESessionEventStatus.NotScheduled
        case "canceled":
            return ESessionEventStatus.Canceled
        case "rescheduled":
            return ESessionEventStatus.Canceled
        default:
            return null
    }
}

function getSessionSlugEnumFromType(type: TSessionTypeSlugDto): ESessionTypeSlug {
    switch (type) {
        case "individual_bonus":
            return ESessionTypeSlug.IndividualBonus
        case "individual_coaching":
            return ESessionTypeSlug.IndividualCoaching
        case "individual_onboarding":
            return ESessionTypeSlug.IndividualOnboarding
        case "individual_leadership_onboarding":
            return ESessionTypeSlug.IndividualLeadershipOnboarding
        case "individual_leadership_coaching":
            return ESessionTypeSlug.IndividualLeadershipCoaching
        case "individual_executive_onboarding":
            return ESessionTypeSlug.IndividualExecutiveOnboarding
        case "individual_executive_coaching":
            return ESessionTypeSlug.IndividualExecutiveCoaching
        case "group_onboarding":
            return ESessionTypeSlug.GroupOnboarding
        case "group_coaching":
            return ESessionTypeSlug.GroupCoaching
        case "group_leadership_onboarding":
            return ESessionTypeSlug.GroupLeadershipOnboarding
        case "group_leadership_coaching":
            return ESessionTypeSlug.GroupLeadershipCoaching
        case "group_executive_onboarding":
            return ESessionTypeSlug.GroupExecutiveOnboarding
        case "group_executive_coaching":
            return ESessionTypeSlug.GroupExecutiveCoaching
        case "spot_coaching_workshop":
            return ESessionTypeSlug.SpotCoachingWorkshop
        case null:
        default:
            return null
    }
}

function sessionBaseAdapter(dto: ISessionBaseDto): ISessionBaseModel {
    return isEmpty(dto)
        ? null
        : {
              allowedMinDaysToEdit: dto.min_days_to_edit,
              isRescheduleAllowed: dto.allow_reschedule,
              coachId: dto.coach_id,
              cohortModality: getCohortModalityEnumFromType(dto.cohort_modality),
              cancelledAt: dto.cancelled_at,
              cancelledBy: dto.cancelled_by,
              enrollmentRank: dto.enrollment_rank,
              isExtraPartySession: dto.is_extra_party_session,
              extraParties: extraPartiesAdapter(dto.extra_parties_info),
              isCancelled: !isEmpty(dto.cancelled_at) && !isEmpty(dto.cancelled_by),
              isManual: dto.is_manual,
              isGift: Boolean(dto.is_gift_session),
              isGifted: Boolean(dto.is_gifted),
              isMissed: dto.is_missed,
              joinUrl: dto.join_url,
              modality: getSessionModalityEnumFromType(dto.modality),
              productTypeId: dto.product_type_id,
              productTypeInternalName: dto.product_type_internal_name,
              rescheduleHistory: isEmpty(dto.reschedule_history)
                  ? []
                  : map<ISessionRescheduleRecordDto, ISessionRescheduleRecordModel>(
                        dto.reschedule_history,
                        ({
                            id,
                            from_date: dateFrom,
                            to_date: dateTo,
                            rescheduled_at: rescheduledAt,
                            rescheduled_by: rescheduledBy
                        }: ISessionRescheduleRecordDto): ISessionRescheduleRecordModel => ({
                            id,
                            dateFrom,
                            dateTo,
                            rescheduledAt,
                            rescheduledBy
                        })
                    ),
              schedulingWindowEnd: dto.scheduling_window_end,
              schedulingWindowStart: dto.scheduling_window_start,
              sessionId: dto.session_id,
              sessionTime: dto.session_time,
              sessionDuration: dto.session_type.duration,
              sessionName: getSessionNameEnumFromType(dto.session_type.name),
              sessionSlug: getSessionSlugEnumFromType(dto.session_type.slug),
              preferredSessionName: "name" in dto.session_type_pref_lang ? dto.session_type_pref_lang.name : null
          }
}

function coachingSessionAdapter(dto: ICoachingSessionDto): ICoachingSessionModel {
    const _isGroup: boolean = eq(dto.modality, ESessionModality.Group)

    return {
        ...sessionBaseAdapter(dto),
        ...(_isGroup
            ? { groupId: dto.id, groupName: dto.group_name, id: null, participantId: null }
            : { groupId: null, groupName: null, id: dto.id, participantId: dto.id }),
        isSwapAllowed: Boolean(dto.allow_swap),
        isAttendanceMarked: Boolean(dto.marked_attendance),
        isModuleReflectionPassed: Boolean(dto.module_reflection_passed),
        cohortName: dto.cohort,
        coach: coachingSessionCoachAdapter(dto.coach),
        participants: coachingSessionParticipantsAdapter(dto.participants),
        eventStatus: getSessionEventStatusEnumFromType(dto.event_status),
        moduleId: isEmpty(dto.module) ? null : dto.module.id,
        moduleTitle: isEmpty(dto.module) ? null : dto.module.title_data
    }
}

function coachingSessionsAdapter(dto: ICoachingSessionsQueryDto): ICoachingSessionsQueryModel {
    return isEmpty(dto)
        ? null
        : {
              liveSession: isEmpty(dto.live_session) ? null : coachingSessionAdapter(dto.live_session),
              unscheduledSessions: isEmpty(dto.not_scheduled_sessions)
                  ? []
                  : map<ICoachingSessionDto, ICoachingSessionModel>(dto.not_scheduled_sessions, coachingSessionAdapter),
              upcomingSessions: isEmpty(dto.upcoming_sessions)
                  ? []
                  : map<ICoachingSessionDto, ICoachingSessionModel>(dto.upcoming_sessions, coachingSessionAdapter),
              pastSessions: isEmpty(dto.passed_sessions)
                  ? []
                  : map<ICoachingSessionDto, ICoachingSessionModel>(dto.passed_sessions, coachingSessionAdapter)
          }
}

function attendanceSessionAdapter(dto: IAttendanceSessionDto): IAttendanceSessionModel {
    return isEmpty(dto)
        ? null
        : {
              ...sessionBaseAdapter(dto),
              id: dto.id,
              isLast: dto.is_last_session,
              cohort: attendanceSessionCohortAdapter(dto.cohort),
              participants: isEmpty(dto.participants_info)
                  ? []
                  : map<IAttendanceSessionParticipantDto, IAttendanceSessionParticipantModel>(
                        dto.participants_info,
                        attendanceSessionParticipantAdapter
                    )
          }
}

function coachPaginatedSessionAdapter(session: ICoachPaginatedSessionDto): ICoachPaginatedSessionModel {
    return {
        ...sessionBaseAdapter(session),
        id: session.id, // if individual session -> participant.id, otherwise -> group.id
        participants: coachSessionParticipantsAdapter(session.participants_info?.participants),
        cohortName: session.cohort,
        groupName: isEmpty(session.participants_info?.title) ? null : session.participants_info.title
    }
}

function groupSessionSummaryConfigAdapter(dto: IGroupSessionSummaryConfigDto): IGroupSessionSummaryConfigModel {
    return isEmpty(dto)
        ? null
        : {
              isSummaryAvailable: dto.is_summary_available,
              areModulesEnabled: dto.are_modules_enabled,
              withSummaryUnavailableWarning: dto.with_summary_unavailable_warning
          }
}

export {
    getSessionInvitationStatusEnumFromType,
    getSessionAttendanceStatusEnumFromType,
    getSessionModalityEnumFromType,
    getSessionEventStatusEnumFromType,
    getSessionSlugEnumFromType,
    sessionBaseAdapter,
    coachingSessionsAdapter,
    coachingSessionAdapter,
    attendanceSessionAdapter,
    coachPaginatedSessionAdapter,
    groupSessionSummaryConfigAdapter
}
